import { spinnerArcsMarkup } from './search-spinner';

/**
 * The screen that covers the walk from "Accept" to a world that exists
 * (2026-09-08).
 *
 * User, with a capture of an Arena entry: "Found ARENA mode game and this is
 * what I see" — a black frame, a crosshair, an empty health plate, and the
 * mouse-look prompt. Reproduced exactly, twice, and it is not a rendering
 * failure: it is the gap between mounting the canvas and the layout, the
 * bodies and the HUD's own numbers arriving. Measured at ~1.7s on a warm
 * local cache over SwiftShader; it is the model fetches, so a real player on
 * a cold cache waits longer, and the dungeon — which loads a whole floor
 * rather than one room — waits longer still.
 *
 * What made it read as breakage rather than loading is that the chrome
 * arrives first. A crosshair over nothing, an empty health bar and an
 * instruction to click and look around are all promises that the world is
 * already there. So this covers them: nothing is shown until there is
 * something to show.
 *
 * **The step line is the load-bearing part, not the ring.** `app.css` turns
 * both arcs off under `prefers-reduced-motion` — the search overlay can
 * afford that because its counter still ticks, and a veil with a frozen ring
 * and one fixed sentence would be indistinguishable from a hung client. Each
 * step here is set from a real milestone (socket joined, geometry built,
 * bodies in), so the screen changes under motion-reduced settings too.
 */
export class EntryVeil {
  private readonly root: HTMLDivElement;
  private readonly stepEl: HTMLParagraphElement;
  private dismissed = false;
  private removeTimer: ReturnType<typeof setTimeout> | null = null;

  /** How long the fade-out runs; must match the duration class below. */
  private static readonly FADE_MS = 420;

  constructor(container: HTMLElement, title: string, firstStep: string, options: { accent?: 'gold' | 'cyan' } = {}) {
    this.root = document.createElement('div');
    // Above the run menu (z-1500), which is the highest thing the run screens
    // mount — nothing should be reachable through a screen that exists to say
    // the game is not ready yet.
    this.root.className = [
      'fixed', 'inset-0', 'z-[1600]', 'bg-lobby',
      'flex', 'flex-col', 'items-center', 'justify-center', 'gap-6', 'p-6',
      'select-none', 'transition-opacity', 'duration-[420ms]', 'ease-out',
    ].join(' ');
    // Announced as one region so a screen reader reads "Entering the Arena,
    // building the pit" rather than racing the two nodes.
    this.root.setAttribute('role', 'status');
    this.root.setAttribute('aria-live', 'polite');

    const heading = document.createElement('h2');
    heading.className = `font-heading ${options.accent === 'cyan' ? 'text-[#2ee6ff]' : 'text-gold'} text-xl sm:text-2xl tracking-wide text-center`;
    heading.textContent = title;

    const ring = document.createElement('div');
    ring.className = 'relative w-[136px] h-[136px]';
    // Cyan is the reconnect veil: the same wait, said in the colour of "the
    // connection", so it never reads as starting a new descent.
    ring.innerHTML = options.accent === 'cyan' ? spinnerArcsMarkup('#2ee6ff', '#1b7f8f') : spinnerArcsMarkup();

    this.stepEl = document.createElement('p');
    this.stepEl.className = 'text-[13px] text-text-dim text-center min-h-[1.25rem]';
    this.stepEl.textContent = firstStep;

    this.root.append(heading, ring, this.stepEl);
    container.appendChild(this.root);
  }

  /**
   * Names what is happening now.
   *
   * Ignored once dismissed: the milestones that drive this are async, and a
   * late one landing after the world is up would otherwise re-label a screen
   * that is already fading out.
   */
  setStep(step: string): void {
    if (this.dismissed) return;
    this.stepEl.textContent = step;
  }

  /** Whether the veil is still covering the screen — the caller's guard against dismissing twice. */
  get isVisible(): boolean {
    return !this.dismissed;
  }

  /**
   * Fades out and removes itself.
   *
   * A fade rather than a cut because the frame underneath is a fully drawn
   * world by the time this is called (the caller waits for a rendered frame),
   * and cutting to it lands harder than the moment deserves.
   */
  dismiss(): void {
    if (this.dismissed) return;
    this.dismissed = true;
    this.root.classList.add('opacity-0', 'pointer-events-none');
    this.removeTimer = setTimeout(() => { this.root.remove(); this.removeTimer = null; }, EntryVeil.FADE_MS);
  }

  /** Torn down with the run screen — a mid-fade unmount must not leave a timer holding a detached node. */
  dispose(): void {
    this.dismissed = true;
    if (this.removeTimer) { clearTimeout(this.removeTimer); this.removeTimer = null; }
    this.root.remove();
  }
}
